import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

/**
 * AI Chat Context Provider
 *
 * Manages global chat state including:
 * - Chat visibility (open/closed/minimized)
 * - Conversation ID for session continuity
 * - Message history (in-memory only for privacy)
 * - Loading states
 */

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
  error?: string;
}

interface AIChatContextType {
  // Chat state
  isOpen: boolean;
  isMinimized: boolean;
  messages: ChatMessage[];
  conversationId: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  openChat: () => void;
  closeChat: () => void;
  minimizeChat: () => void;
  toggleChat: () => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  setConversationId: (id: string) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearMessages: () => void;
  clearError: () => void;
}

const AIChatContext = createContext<AIChatContextType | undefined>(undefined);

export const useAIChatContext = () => {
  const context = useContext(AIChatContext);
  if (!context) {
    throw new Error('useAIChatContext must be used within AIChatProvider');
  }
  return context;
};

interface AIChatProviderProps {
  children: React.ReactNode;
}

export const AIChatProvider: React.FC<AIChatProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Message ID counter for unique IDs
  const messageIdCounter = useRef(0);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
  }, []);

  const minimizeChat = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const toggleChat = useCallback(() => {
    if (isMinimized) {
      setIsMinimized(false);
    } else if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  }, [isOpen, isMinimized]);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>): string => {
    const id = `msg-${Date.now()}-${++messageIdCounter.current}`;
    const newMessage: ChatMessage = {
      ...message,
      id,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
    return id;
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AIChatContextType = {
    isOpen,
    isMinimized,
    messages,
    conversationId,
    isLoading,
    error,
    openChat,
    closeChat,
    minimizeChat,
    toggleChat,
    addMessage,
    updateMessage,
    setConversationId,
    setIsLoading,
    setError,
    clearMessages,
    clearError,
  };

  return (
    <AIChatContext.Provider value={value}>
      {children}
    </AIChatContext.Provider>
  );
};

export default AIChatProvider;
